import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, Grid, Paper,
  Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import {
  fetchRoles, saveRole, deleteRole, fetchRolePermissions,
  assignPermissionsToRole, setSelectedRole, clearRoleData, clearRoleMessages,
} from '../reduxStore/RoleSlice';
import { fetchPermissions, clearPermissionData } from '../reduxStore/PermissionSlice';
import {
  fetchRoleScopes, setRoleScopes, clearScopeData, clearScopeMessages,
} from '../reduxStore/ScopeSlice';
import { fetchDepartements } from '../reduxStore/DepartementSlice';
import { fetchGeneriqueDataByType } from '../reduxStore/GeneriqueDataSlice';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import { Role, Permission } from '../models/models';
import { ScopeType } from '../constants/enums';
import { permissionActionLabel, permissionModuleAide, permissionModuleLabel } from '../constants/permissionLabels';
import MyTable from '../components/MyTable';
import { TableHeader, TableAction } from '../constants/interfaces';
import { ButtonType, GeneriqueDataType } from '../constants/enums';
import { useSnackbar } from '../components/SnackbarProvider';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import { groupByDept } from '../utils/scopeGrouping';

const SCOPE_LABELS: Record<ScopeType, string> = {
  DEPARTEMENT:         'Départements',
  DEPOT:               'Dépôts',
  CAISSE:              'Caisses',
  TYPE_DOCUMENT:       'Types de document',
  RESTRICT_TO_CREATOR: 'Restriction propriétaire',
};

const GestionRoles: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { roleData, total, loading, error, successMessage, rolePermissions } =
    useSelector((state: RootState) => state.RoleSlice);
  const { permissionData } = useSelector((state: RootState) => state.PermissionSlice);
  const {
    roleScopesDept, roleScopesDepot, roleScopesCaisse, roleScopesTypeDoc, roleScopesRestrictToCreator,
    error: scopeError, successMessage: scopeSuccess,
  } = useSelector((state: RootState) => state.ScopeSlice);
  const { departements }          = useSelector((state: RootState) => state.DepartementSlice);
  const { depotData, caisseData } = useSelector((state: RootState) => state.GeneriqueDataSlice);
  const { sysTypeData }           = useSelector((state: RootState) => state.SysTypeDocumentSlice);
  const { showSnackbar } = useSnackbar();

  const [fetchParams, setFetchParams] = useState({ limit: 10, offset: 0 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [form, setForm] = useState<Role>({ nom: '', description: '' });
  const [selectedPermIds, setSelectedPermIds] = useState<number[]>([]);

  const [selectedDeptIds,    setSelectedDeptIds]    = useState<number[]>([]);
  const [selectedDepotIds,   setSelectedDepotIds]   = useState<number[]>([]);
  const [selectedCaisseIds,  setSelectedCaisseIds]  = useState<number[]>([]);
  const [selectedTypeDocIds, setSelectedTypeDocIds] = useState<number[]>([]);
  const [selectedRestrictToCreator, setSelectedRestrictToCreator] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchRoles(fetchParams));
    dispatch(fetchPermissions({ limit: 500, offset: 0 }));
    dispatch(fetchDepartements());
    dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.DEPOT }));
    dispatch(fetchGeneriqueDataByType({ type: GeneriqueDataType.CAISSE }));
    dispatch(fetchSysTypeDocuments());
    return () => {
      dispatch(clearRoleData());
      dispatch(clearPermissionData());
      dispatch(clearScopeData());
      dispatch(setSelectedRole(null));
    };
  }, [dispatch, fetchParams, successMessage]);

  useEffect(() => {
    if (error) showSnackbar(error, 'error');
    if (successMessage) showSnackbar(successMessage, 'success');
    if (error || successMessage) dispatch(clearRoleMessages());
  }, [error, successMessage, dispatch, showSnackbar]);

  useEffect(() => {
    if (scopeError)   showSnackbar(scopeError, 'error');
    if (scopeSuccess) showSnackbar(scopeSuccess, 'success');
    if (scopeError || scopeSuccess) dispatch(clearScopeMessages());
  }, [scopeError, scopeSuccess, dispatch, showSnackbar]);

  useEffect(() => { setSelectedPermIds(rolePermissions); }, [rolePermissions]);
  useEffect(() => { setSelectedDeptIds(roleScopesDept);       }, [roleScopesDept]);
  useEffect(() => { setSelectedDepotIds(roleScopesDepot);     }, [roleScopesDepot]);
  useEffect(() => { setSelectedCaisseIds(roleScopesCaisse);   }, [roleScopesCaisse]);
  useEffect(() => { setSelectedTypeDocIds(roleScopesTypeDoc); }, [roleScopesTypeDoc]);
  useEffect(() => { setSelectedRestrictToCreator(roleScopesRestrictToCreator); }, [roleScopesRestrictToCreator]);

  const handleNew = () => {
    setForm({ nom: '', description: '' });
    setSelectedPermIds([]);
    setSelectedDeptIds([]);
    setSelectedDepotIds([]);
    setSelectedCaisseIds([]);
    setSelectedTypeDocIds([]);
    setSelectedRestrictToCreator(false);
    dispatch(setSelectedRole(null));
    setDialogOpen(true);
  };

  const handleEdit = (role: Role) => {
    setForm(role);
    dispatch(setSelectedRole(role));
    if (role.id) {
      dispatch(fetchRolePermissions(role.id));
      dispatch(fetchRoleScopes({ role_id: role.id, scope_type: ScopeType.DEPARTEMENT }));
      dispatch(fetchRoleScopes({ role_id: role.id, scope_type: ScopeType.DEPOT }));
      dispatch(fetchRoleScopes({ role_id: role.id, scope_type: ScopeType.CAISSE }));
      dispatch(fetchRoleScopes({ role_id: role.id, scope_type: ScopeType.TYPE_DOCUMENT }));
      dispatch(fetchRoleScopes({ role_id: role.id, scope_type: ScopeType.RESTRICT_TO_CREATOR }));
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const result = await dispatch(saveRole(form));
    if (saveRole.fulfilled.match(result)) {
      const savedRole = result.payload as Role;
      if (savedRole.id) {
        await dispatch(assignPermissionsToRole({ role_id: savedRole.id, permission_ids: selectedPermIds }));
        if (!form.id) setForm(prev => ({ ...prev, id: savedRole.id }));
      }
      setDialogOpen(false);
    }
  };

  const handleSaveScopes = async () => {
    if (!form.id) return;
    await dispatch(setRoleScopes({ role_id: form.id, scope_type: ScopeType.DEPARTEMENT,   scope_ids: selectedDeptIds }));
    await dispatch(setRoleScopes({ role_id: form.id, scope_type: ScopeType.DEPOT,         scope_ids: selectedDepotIds }));
    await dispatch(setRoleScopes({ role_id: form.id, scope_type: ScopeType.CAISSE,        scope_ids: selectedCaisseIds }));
    await dispatch(setRoleScopes({ role_id: form.id, scope_type: ScopeType.TYPE_DOCUMENT, scope_ids: selectedTypeDocIds }));
    await dispatch(setRoleScopes({ role_id: form.id, scope_type: ScopeType.RESTRICT_TO_CREATOR, scope_ids: selectedRestrictToCreator ? [0] : [] }));
  };

  const handleDelete = async (role: Role) => {
    if (!role.id) return;
    if (!window.confirm(`Supprimer le rôle "${role.nom}" ?`)) return;
    dispatch(deleteRole(role.id));
  };

  const togglePerm = (id: number) => {
    setSelectedPermIds(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const handlePageChange = (page: number) => {
    setFetchParams(prev => ({ ...prev, offset: (page - 1) * prev.limit }));
  };

  const permsByModule = permissionData.reduce<Record<string, Permission[]>>((acc, p) => {
    if (!acc[p.module]) acc[p.module] = [];
    acc[p.module].push(p);
    return acc;
  }, {});

  const headers: TableHeader<Role>[] = [
    { key: 'nom',         label: 'Nom du rôle' },
    { key: 'description', label: 'Description' },
  ];

  const actions: TableAction<Role>[] = [
    { type: ButtonType.EDIT,   label: 'Modifier',   callback: handleEdit },
    { type: ButtonType.DELETE, label: 'Supprimer',  callback: handleDelete },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title="Gestion des rôles"
        subtitle={`${total} rôle${total > 1 ? 's' : ''}`}
        icon={<AdminPanelSettingsIcon />}
        addLabel="Nouveau rôle"
        onAdd={handleNew}
      />

      {loading && <CircularProgress sx={{ mb: 2 }} />}

      <AppSurface>
        <MyTable
          headers={headers}
          data={roleData}
          actions={actions}
          onPageChange={handlePageChange}
          totalRows={total}
        />
      </AppSurface>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? 'Modifier le rôle' : 'Nouveau rôle'}</DialogTitle>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Rôle & Permissions" />
          <Tab label="Restrictions de scope" disabled={!form.id} />
        </Tabs>
        <DialogContent>
          {activeTab === 0 && <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nom du rôle" fullWidth required
                value={form.nom}
                onChange={e => setForm(prev => ({ ...prev, nom: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description" fullWidth
                value={form.description ?? ''}
                onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="caption">Permissions</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={permissionData.length > 0 && permissionData.every(p => selectedPermIds.includes(p.id))}
                        indeterminate={permissionData.some(p => selectedPermIds.includes(p.id)) && !permissionData.every(p => selectedPermIds.includes(p.id))}
                        onChange={() => {
                          const allIds = permissionData.map(p => p.id);
                          const allSelected = allIds.every(id => selectedPermIds.includes(id));
                          setSelectedPermIds(allSelected ? [] : allIds);
                        }}
                      />
                    }
                    label={<Typography variant="caption">Tout sélectionner</Typography>}
                  />
                </Box>
              </Divider>
              {Object.entries(permsByModule).map(([module, perms]) => {
                const moduleIds = perms.map(p => p.id);
                const allModuleSelected = moduleIds.every(id => selectedPermIds.includes(id));
                const someModuleSelected = moduleIds.some(id => selectedPermIds.includes(id));

                const isDocVentes = module === 'docVentes';
                const globalPerms = isDocVentes ? perms.filter(p => p.code.startsWith('docVentes.')) : [];
                const byDocType = isDocVentes
                  ? perms.filter(p => p.code.startsWith('documentVentes.')).reduce<Record<string, Permission[]>>((acc, p) => {
                      const type = p.code.split('.')[1];
                      if (!acc[type]) acc[type] = [];
                      acc[type].push(p);
                      return acc;
                    }, {})
                  : {};

                const renderPerms = (list: Permission[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {list.map(p => (
                      <FormControlLabel
                        key={p.id}
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedPermIds.includes(p.id)}
                            onChange={() => togglePerm(p.id)}
                          />
                        }
                        label={<Tooltip title={p.description}><Chip label={permissionActionLabel(p)} size="small" /></Tooltip>}
                      />
                    ))}
                  </Box>
                );
                const aide = permissionModuleAide(module);

                return (
                  <Paper key={module} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Checkbox
                        size="small"
                        checked={allModuleSelected}
                        indeterminate={someModuleSelected && !allModuleSelected}
                        onChange={() => {
                          if (allModuleSelected) {
                            setSelectedPermIds(prev => prev.filter(id => !moduleIds.includes(id)));
                          } else {
                            setSelectedPermIds(prev => prev.concat(moduleIds.filter(id => !prev.includes(id))));
                          }
                        }}
                      />
                      <Typography variant="subtitle2" fontWeight={600}>
                        {permissionModuleLabel(module)}
                      </Typography>
                    </Box>
                    {aide && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, pl: 1 }}>
                        {aide}
                      </Typography>
                    )}

                    {isDocVentes ? (
                      <Box sx={{ pl: 1 }}>
                        {globalPerms.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Global (tous types)
                            </Typography>
                            {renderPerms(globalPerms)}
                          </Box>
                        )}
                        {Object.entries(byDocType).map(([docType, typePerms]) => {
                          const typeIds = typePerms.map(p => p.id);
                          const allType = typeIds.every(id => selectedPermIds.includes(id));
                          const someType = typeIds.some(id => selectedPermIds.includes(id));
                          return (
                            <Box key={docType} sx={{ mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Checkbox
                                  size="small"
                                  checked={allType}
                                  indeterminate={someType && !allType}
                                  onChange={() => {
                                    if (allType) {
                                      setSelectedPermIds(prev => prev.filter(id => !typeIds.includes(id)));
                                    } else {
                                      setSelectedPermIds(prev => prev.concat(typeIds.filter(id => !prev.includes(id))));
                                    }
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                  {docType}
                                </Typography>
                              </Box>
                              <Box sx={{ pl: 2 }}>
                                {renderPerms(typePerms)}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 1 }}>
                        {perms.map(p => (
                          <FormControlLabel
                            key={p.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedPermIds.includes(p.id)}
                                onChange={() => togglePerm(p.id)}
                              />
                            }
                            label={<Tooltip title={p.description}><Chip label={permissionActionLabel(p)} size="small" /></Tooltip>}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Grid>
          </Grid>}

          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Cochez les éléments auxquels ce rôle <strong>n'aura PAS accès</strong>.
                Si rien n'est coché, l'accès est libre pour tous les éléments de ce type.
              </Typography>

              <Box sx={{
                p: 1.5, mb: 2, borderRadius: 1,
                bgcolor: selectedRestrictToCreator ? 'error.light' : 'action.hover',
                border: '1px solid',
                borderColor: selectedRestrictToCreator ? 'error.main' : 'divider',
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedRestrictToCreator}
                      onChange={(e) => setSelectedRestrictToCreator(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {SCOPE_LABELS.RESTRICT_TO_CREATOR}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Si activé, les utilisateurs avec ce rôle ne verront que les documents qu'ils ont eux-mêmes créés.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {SCOPE_LABELS.DEPARTEMENT}
                    </Typography>
                    {departements.length === 0
                      ? <Typography variant="caption" color="text.secondary">Aucun département</Typography>
                      : departements.map(d => (
                        <FormControlLabel key={d.id}
                          control={
                            <Checkbox size="small"
                              checked={selectedDeptIds.includes(d.id!)}
                              onChange={() => setSelectedDeptIds(prev =>
                                prev.includes(d.id!) ? prev.filter(x => x !== d.id) : [...prev, d.id!]
                              )}
                              sx={{ color: 'success.main', '&.Mui-checked': { color: 'error.main' } }}
                            />
                          }
                          label={
                            <Typography variant="body2" fontWeight={500}
                              color={selectedDeptIds.includes(d.id!) ? 'error.main' : 'success.main'}>
                              {d.nom}
                            </Typography>
                          }
                        />
                      ))
                    }
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {SCOPE_LABELS.DEPOT}
                    </Typography>
                    {depotData.length === 0
                      ? <Typography variant="caption" color="text.secondary">Aucun dépôt</Typography>
                      : groupByDept(depotData, departements).map(g => {
                        const deptBlocked = g.dept ? selectedDeptIds.includes(g.dept.id!) : false;
                        return (
                          <Box key={g.dept?.id ?? 'shared'} sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, mb: 0.25 }}>
                              {g.dept ? g.dept.nom : 'Partagé'}{deptBlocked && ' (dept bloqué)'}
                            </Typography>
                            {g.items.map(d => (
                              <FormControlLabel key={d.id}
                                disabled={deptBlocked}
                                sx={{ opacity: deptBlocked ? 0.5 : 1, ml: 0.5 }}
                                control={
                                  <Checkbox size="small"
                                    checked={selectedDepotIds.includes(d.id!)}
                                    onChange={() => setSelectedDepotIds(prev =>
                                      prev.includes(d.id!) ? prev.filter(x => x !== d.id) : [...prev, d.id!]
                                    )}
                                    sx={{ color: 'success.main', '&.Mui-checked': { color: 'error.main' } }}
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={500}
                                    color={selectedDepotIds.includes(d.id!) ? 'error.main' : 'success.main'}>
                                    {d.nom}
                                  </Typography>
                                }
                              />
                            ))}
                          </Box>
                        );
                      })
                    }
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {SCOPE_LABELS.CAISSE}
                    </Typography>
                    {caisseData.length === 0
                      ? <Typography variant="caption" color="text.secondary">Aucune caisse</Typography>
                      : groupByDept(caisseData, departements).map(g => {
                        const deptBlocked = g.dept ? selectedDeptIds.includes(g.dept.id!) : false;
                        return (
                          <Box key={g.dept?.id ?? 'shared'} sx={{ mb: 1 }}>
                            <Typography variant="caption" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.7rem', color: 'text.secondary', mt: 0.5, mb: 0.25 }}>
                              {g.dept ? g.dept.nom : 'Partagé'}{deptBlocked && ' (dept bloqué)'}
                            </Typography>
                            {g.items.map(c => (
                              <FormControlLabel key={c.id}
                                disabled={deptBlocked}
                                sx={{ opacity: deptBlocked ? 0.5 : 1, ml: 0.5 }}
                                control={
                                  <Checkbox size="small"
                                    checked={selectedCaisseIds.includes(c.id!)}
                                    onChange={() => setSelectedCaisseIds(prev =>
                                      prev.includes(c.id!) ? prev.filter(x => x !== c.id) : [...prev, c.id!]
                                    )}
                                    sx={{ color: 'success.main', '&.Mui-checked': { color: 'error.main' } }}
                                  />
                                }
                                label={
                                  <Typography variant="body2" fontWeight={500}
                                    color={selectedCaisseIds.includes(c.id!) ? 'error.main' : 'success.main'}>
                                    {c.nom}
                                  </Typography>
                                }
                              />
                            ))}
                          </Box>
                        );
                      })
                    }
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {SCOPE_LABELS.TYPE_DOCUMENT}
                    </Typography>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                      columnGap: 2,
                      rowGap: 0.5,
                    }}>
                      {sysTypeData.length === 0
                        ? <Typography variant="caption" color="text.secondary">Aucun type</Typography>
                        : sysTypeData.map(t => (
                          <FormControlLabel key={t.id}
                            sx={{ m: 0 }}
                            control={
                              <Checkbox size="small"
                                checked={selectedTypeDocIds.includes(t.id)}
                                onChange={() => setSelectedTypeDocIds(prev =>
                                  prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id]
                                )}
                                sx={{ color: 'success.main', '&.Mui-checked': { color: 'error.main' } }}
                              />
                            }
                            label={
                              <Typography variant="body2" fontWeight={500} noWrap
                                color={selectedTypeDocIds.includes(t.id) ? 'error.main' : 'success.main'}>
                                {t.libelle}
                              </Typography>
                            }
                          />
                        ))
                      }
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          {activeTab === 0 && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>
              Enregistrer
            </Button>
          )}
          {activeTab === 1 && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveScopes}>
              Enregistrer les scopes
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AppPage>
  );
};

export default GestionRoles;
